import { useMemo } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Detects if the visitor is likely from the US based on browser locale/timezone.
 * Returns the appropriate currency symbol.
 * Exchange rate: 1 USD = 1 EUR (1:1 parity).
 */
export function useCurrency() {
  const { language } = useLanguage();

  const isUS = useMemo(() => {
    if (language !== "en") return false;

    // Check browser language for US locale
    const lang = navigator.language || (navigator as any).userLanguage || "";
    if (lang === "en-US") return true;

    // Check timezone as fallback
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz?.startsWith("America/") && !tz.includes("Buenos_Aires") && !tz.includes("Sao_Paulo") && !tz.includes("Mexico")) {
        // Broad US timezone match (covers most US timezones)
        const usTimezones = [
          "America/New_York", "America/Chicago", "America/Denver",
          "America/Los_Angeles", "America/Phoenix", "America/Anchorage",
          "Pacific/Honolulu", "America/Detroit", "America/Indiana",
          "America/Boise", "America/Juneau", "America/Adak",
        ];
        return usTimezones.some((usTz) => tz.startsWith(usTz.split("/")[0] + "/" + usTz.split("/")[1]));
      }
    } catch {
      // Ignore timezone detection errors
    }

    return false;
  }, [language]);

  const currencySymbol = isUS ? "$" : "€";
  const currencyPosition: "before" | "after" = isUS ? "before" : "after";

  const formatPrice = (amount: number, suffix?: string): string => {
    if (isUS) {
      return suffix ? `$${amount}${suffix}` : `$${amount}`;
    }
    return suffix ? `${amount}€${suffix}` : `${amount}€`;
  };

  const formatUnitPrice = (amount: number, unit: string): string => {
    if (isUS) {
      return `$${amount} / ${unit}`;
    }
    return `${amount}€ / ${unit}`;
  };

  return { isUS, currencySymbol, currencyPosition, formatPrice, formatUnitPrice };
}
